/**
 * Auto-fills a new sales order's shipping and billing address fields.
 *
 * A render-less effect component: for a new order it populates the billing
 * and shipping address fields from the account's primary saved addresses,
 * preferring the site-group address and falling back to the customer one,
 * and clears the fields when no valid saved address is available.
 *
 * Contains:
 * - AddressAutoPopulator — effect-only shipping/billing address auto-fill.
 */
import { useFormikContext } from 'formik';
import { useEffect } from 'react';

const AddressAutoPopulator = ({
  siteGroupPrimaryBillAddress,
  customerPrimaryBillAddress,
  siteGroupPrimaryShipAddress,
  customerPrimaryShipAddress,
  liveCustomerId,
  liveSiteGroupId,
}) => {
  const { values, setFieldValue } = useFormikContext();

  // Helper function to check if an address has actual content (not just empty fields)
  const isValidAddress = address =>
    address != null &&
    ((address.address_1 && address.address_1.trim() !== '') ||
      (address.city && address.city.trim() !== '') ||
      (address.state && address.state.trim() !== '') ||
      (address.zipcode && address.zipcode.trim() !== ''));

  // Function to determine which addresses are available and valid
  const getAddressAvailability = () => {
    const hasSiteGroupBilling = values.site_group === liveSiteGroupId && isValidAddress(siteGroupPrimaryBillAddress);
    const hasCustomerBilling = values.customer === liveCustomerId && isValidAddress(customerPrimaryBillAddress);
    const hasSiteGroupShipping = values.site_group === liveSiteGroupId && isValidAddress(siteGroupPrimaryShipAddress);
    const hasCustomerShipping = values.customer === liveCustomerId && isValidAddress(customerPrimaryShipAddress);

    return {
      hasSiteGroupBilling,
      hasCustomerBilling,
      hasSiteGroupShipping,
      hasCustomerShipping,
      hasAnyValidAddress: hasSiteGroupBilling || hasCustomerBilling || hasSiteGroupShipping || hasCustomerShipping,
    };
  };

  // Function to clear all address fields
  const clearAllAddresses = () => {
    const addressFields = [
      'bill_address',
      'bill_city',
      'bill_state',
      'bill_zip',
      'bill_country',
      'ship_address',
      'ship_city',
      'ship_state',
      'ship_zip',
      'ship_country',
    ];

    addressFields.forEach(field => {
      setFieldValue(field, '');
    });
  };

  // Function to select the best billing address using fallback strategy
  const selectBillingAddress = () => {
    // Priority: Site group first, then customer fallback
    if (values.site_group === liveSiteGroupId && isValidAddress(siteGroupPrimaryBillAddress)) {
      return siteGroupPrimaryBillAddress;
    }
    if (values.customer === liveCustomerId && isValidAddress(customerPrimaryBillAddress)) {
      return customerPrimaryBillAddress;
    }
    return null;
  };

  // Function to select the best shipping address using fallback strategy
  const selectShippingAddress = () => {
    // Priority: Site group first, then customer fallback
    if (values.site_group === liveSiteGroupId && isValidAddress(siteGroupPrimaryShipAddress)) {
      return siteGroupPrimaryShipAddress;
    }
    if (values.customer === liveCustomerId && isValidAddress(customerPrimaryShipAddress)) {
      return customerPrimaryShipAddress;
    }
    return null;
  };

  // Function to populate billing address fields
  const populateBillingAddress = address => {
    if (!address) return;

    const fullAddress = address.address_1 + (address.address_2 ? ', ' + address.address_2 : '');

    setFieldValue('bill_address', fullAddress);
    setFieldValue('bill_city', address.city);
    setFieldValue('bill_state', address.state);
    setFieldValue('bill_zip', address.zipcode);
    setFieldValue('bill_country', address.country);
  };

  // Function to populate shipping address fields
  const populateShippingAddress = address => {
    if (!address) return;

    const fullAddress = address.address_1 + (address.address_2 ? ', ' + address.address_2 : '');

    setFieldValue('ship_address', fullAddress);
    setFieldValue('ship_city', address.city);
    setFieldValue('ship_state', address.state);
    setFieldValue('ship_zip', address.zipcode);
    setFieldValue('ship_country', address.country);
  };

  useEffect(() => {
    // Only auto-populate for new orders (not when editing existing ones)
    if (values.id != null) return;

    // Get address availability information
    const addressAvailability = getAddressAvailability();

    // Clear all addresses if no valid addresses are available
    if (!addressAvailability.hasAnyValidAddress) {
      clearAllAddresses();
      return;
    }

    // Handle billing address auto-population
    const selectedBillAddress = selectBillingAddress();
    populateBillingAddress(selectedBillAddress);

    // Handle shipping address auto-population
    const selectedShipAddress = selectShippingAddress();
    populateShippingAddress(selectedShipAddress);
  }, [
    values.customer,
    values.site_group,
    values.id,
    siteGroupPrimaryBillAddress,
    customerPrimaryBillAddress,
    siteGroupPrimaryShipAddress,
    customerPrimaryShipAddress,
    liveCustomerId,
    liveSiteGroupId,
    setFieldValue,
  ]);

  return null; // This component doesn't render anything
};

export default AddressAutoPopulator;
